import Vue from 'vue'

// Lib imports
import axios from 'axios'
import CryptoJS from 'crypto-js'
import { decryptWithKey, encryptWithKey } from '../utils/cripto';

const CHECKSUM_SECRET = process.env.VUE_APP_API_SECRET;
export const API_BASE_URL = (process.env.VUE_APP_API_LINK || '/api/')
const CLIENT_ENV = 'web';
const TIMEOUT = 0;

// Web equivalent of device ID generation
async function getDeviceId() {
  try {
    // For web, we'll create a persistent device ID using localStorage
    let deviceId = localStorage.getItem('deviceId');

    if (!deviceId) {
      // Generate a fingerprint based on available browser information
      const fingerprint = [
        navigator.userAgent,
        navigator.hardwareConcurrency,
        navigator.deviceMemory,
        screen.width,
        screen.height,
        navigator.language
      ].join('|');

      // Hash the fingerprint to create a consistent device ID
      deviceId = CryptoJS.SHA256(fingerprint).toString();
      localStorage.setItem('deviceId', deviceId);
    }

    return deviceId;
  } catch (error) {
    console.error('Error generating device ID:', error);
    // Fallback to a random ID if something fails
    return 'web-' + Math.random().toString(36).substring(2) + '-' + Date.now();
  }
}

export async function generateRequestSignature(method, path) {
  try {
    const timestamp = Math.floor(Date.now() / 1000); // Seconds
    const deviceId = await getDeviceId();
    if (!String(path).startsWith('/') && !String(path).startsWith('http')) {
      path = `/${path}`;
    }

    const payload = [
      method.toUpperCase(),
      path,
      timestamp,
      CLIENT_ENV,
      deviceId
    ].join('|');

    return {
      signature: CryptoJS.HmacSHA256(payload, CHECKSUM_SECRET).toString(),
      timestamp,
      deviceId
    };
  } catch (error) {
    console.error('Error generating request signature:', error);
    throw error;
  }
}

const token = localStorage.getItem('user-token')
const key = CHECKSUM_SECRET
const cryptoUses = parseInt(process.env.VUE_APP_USE_CRYPTO) === 1

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Client-Env': CLIENT_ENV,
    'brcorteapp-version': process.env.VUE_APP_VERSION || '1.0.0'
  }
});
Vue.prototype.axios = api;

Vue.prototype.axios.interceptors.request.use(async (config) => {
  const { method, url } = config;
  if (['GET', 'OPTIONS'].includes(method)) return config;
  const path = url.replace(API_BASE_URL, '');
  const { signature, timestamp, deviceId } = await generateRequestSignature(method, path);

  config.headers['X-Request-Timestamp'] = timestamp;
  config.headers['X-Signature'] = signature;
  config.headers['X-Device-Id'] = deviceId;
  // Adiciona o header de versão em todas as requisições
  config.headers = config.headers || {};
  const requestData = config.data || {};

  // Adiciona timestamp aos dados da requisição
  const dataWithTimestamp = {
    ...requestData,
    _timestamp: timestamp
  };
  const skipCrypt = _.has(dataWithTimestamp, 'skipCrypt')

  // Atualiza os dados da requisição com o timestamp
  config.data = dataWithTimestamp;
  let blacklist = [
    'dieta/getFDEmpresa',
    'empresa',
    'dieta/saveFD',
    'dieta/savePD',
    'logs'
  ]

  
  // Do something before request is sent
  if (cryptoUses && !skipCrypt && !url.startsWith('https://') && !blacklist.some((u) => url.startsWith(u))) {
    const encryptedPayload = encryptWithKey(config.data, key)
    config.headers['X-Crypto'] = true;
    config.data = {
      payload: encryptedPayload
    }
  }
  return config;
}, function (error) {
  // Do something with request error
  return Promise.reject(error);
})
Vue.prototype.axios.interceptors.response.use(
  (res) => {
    // 🔓 Se resposta vier criptografada
    if (res.data && (res.data.crypto_res || (res.data.data && res.data.data.crypto_res))) {
      try {
        const payload = res.data.payload ? res.data.payload : res.data.data.payload
        const decryptedData = decryptWithKey(payload, key)
        res = { data: decryptedData }
      } catch (e) {
        throw e
      }
    }
    return res
  },
  (error) => {
    // 🔓 Se erro também vier criptografado
    if (error.response && error.response.data && error.response.data.crypto_res) {
      try {
        const payload = error.response.data.payload
        if (payload) {
          const decryptedData = decryptWithKey(payload, key)
          error.response.data = decryptedData
        }
      } catch (e) {
        console.warn("Falha ao descriptografar erro:", e)
      }
    }
    return Promise.reject(error)
  }
)

if (token) {
  Vue.prototype.axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
}
export default Vue.prototype.axios;
